/**
 * 「이용 내역」 화면에 보이는 앱·서버·역할·대상 종류·이유·화면 이름(10-31 I15 — 영문 코드를 그대로 보이지 않음).
 * 앱·이유 이름은 studio 「이용 내역」(UsageLogSettings APP_LABELS, api/auditLog.ts)과 같은 말. 모르는 값은 한국어 기본값.
 * Vue·axios 를 들이지 않는 순수 모듈이라 node --test 로 시험한다(`__tests__/auditLabels.test.mjs`).
 */
import { CHANGE_FIELD_LABEL, defOf, valueLabel, valueText } from "./securityModes.js";

/** 앱(계약 CLIENT_APPS). 로그인 요청은 어느 앱인지 몰라 'unknown' */
const APP = {
  studio: "Studio",
  "studio-desktop": "데스크톱 앱",
  "admin-hospital": "병원 관리 앱",
  "admin-manufacturer": "제조사 관리 앱",
  "admin-certilife": "서티라이프 운영 화면",
  "admin-reseller": "리셀러 화면",
  "open-api": "Open API",
  unknown: "알 수 없는 앱",
};

/** 서버(계약 SERVICES) */
const SERVICE = {
  inapi: "기본 서버",
  "crm-api": "CRM 서버",
  "counsel-api": "상담 서버",
  "call-api": "전화 서버",
  "ai-core": "AI 서버",
  chatbot: "챗봇 서버",
  websocket: "실시간 서버",
  "contracts-api": "계약 서버",
  "payment-worker": "결제 서버",
  "open-api": "Open API",
  "certificate-api": "인증서 서버",
  mq: "메시지 큐",
  "msg-dispatcher": "발송 서버",
};

/** 계정 역할(actor.role) */
const ROLE = {
  CERTILIFE: "운영자",
  HOSPITAL: "병원",
  MANUFACTURER: "제조사",
  DENTAL_LABORATORY: "기공소",
  STUDIO: "스튜디오",
  RESELLER: "리셀러",
  CUSTOMER: "고객",
  WEBCHAT: "웹채팅 고객",
  EMR_TUNNEL: "EMR 연결",
};

/** 대상 종류(target.type) — inapi·각 서버가 이용 내역에 넣는 값(10-31 grep) */
const TARGET_TYPE = {
  account: "계정",
  company: "회사",
  customer: "고객",
  patient: "환자",
  end_user: "이용자",
  security_setting: "보안 설정",
  audit_event: "이용 내역",
  audit_day: "이용 내역 날짜별 사본",
  audit_check: "이용 내역 점검",
  sender_number: "발신번호",
  route: "요청 경로",
  admin_route: "관리 요청 경로",
  role_template: "권한 틀",
  weblink: "웹 링크",
  scenario: "시나리오",
  flow: "흐름",
  channel: "채널",
  event: "이벤트",
  chatbot: "챗봇",
  chatroom: "상담방",
  conversation: "대화",
  message: "메시지",
  template: "템플릿",
  campaign: "캠페인",
  promotion: "프로모션",
  api_key: "API 키",
  cert: "인증서",
  certificate: "인증서",
  cert_issue_job: "인증서 발급 작업",
  contract: "계약",
  contract_goal: "계약 목표",
  payment_method: "결제 수단",
  payment_history: "결제 내역",
  call: "통화",
  callbot_customer: "콜봇 고객",
  document: "문서",
  knowledge_document: "지식 문서",
  version: "버전",
  extra_tool: "추가 도구",
  export_task: "내보내기 작업",
  admin_data: "운영 데이터",
  all: "전체",
};

/**
 * 실패·거부 이유(reason) — studio api/auditLog.ts AUDIT_REASON_LABELS 와 같은 말.
 * ponytail: 서버에 새 코드가 생기면 「기타」로 보임 — 자주 보이면 여기에 한 줄 더한다
 */
const REASON = {
  LOGIN_FAILED: "로그인 실패",
  UNKNOWN_USERNAME: "없는 아이디",
  ACCOUNT_LOCKED: "계정 잠김",
  ACCOUNT_INACTIVE: "쓸 수 없는 계정",
  GEO_BLOCKED: "해외 접속 막힘",
  LEGACY_ACCESS_BLOCKED: "옛 접속 방식 막힘",
  SESSION_NOT_ALLOWED: "허용되지 않은 로그인 방식",
  IMPERSONATION_REISSUE_REQUIRED: "리셀러 화면에서 다시 들어와야 함",
  MFA_REQUIRED: "2차 인증 필요",
  MFA_ENROLL_REQUIRED: "2차 인증 수단 등록 필요",
  MFA_EXEMPT_NOT_ALLOWED: "2차 인증 예외 중이라 안 됨",
  SEND_AUTH_REQUIRED: "발송 전 인증 필요",
  TWO_FACTOR_NOT_ASSIGNED: "2차 인증 수단 없음",
  TWO_FACTOR_ENFORCED: "2차 인증 필수 계정",
  TWO_FACTOR_REQUIRED_BY_POLICY: "회사 정책상 2차 인증 필수",
  OTP_INVALID: "인증번호 틀림",
  OTP_EXPIRED: "인증번호 만료",
  OTP_RATE_LIMITED: "인증번호 요청이 너무 많음",
  OTP_SEND_FAILED: "인증번호 발송 실패",
  PERMISSION_DENIED: "권한 없음",
  MENU_FORBIDDEN: "메뉴 권한 없음",
  PERM_UNMARKED: "권한 표시가 없는 경로",
  PERM_UNAVAILABLE: "권한 확인 안 됨",
  PERM_LOOKUP_FAILED: "권한 확인 장애",
  PERM_DELEGATION_DENIED: "내 권한 밖",
  PERM_INVALID_CODE: "없는 권한·틀",
  INVALID_PERMISSION_VALUE: "허용되지 않은 권한 값",
  ACCOUNT_SCOPE_FORBIDDEN: "다른 회사 계정·자원",
  NOT_COMPANY_MANAGER: "회사 관리자 아님",
  NOT_OWNER: "대표 아님",
  OWNER_TRANSFER_REQUIRED: "대표 넘기기 필요",
  OPERATOR_ACTING_DENIED: "운영자 대행 안 됨",
  REASON_REQUIRED: "이유 없음",
  ROLE_TEMPLATE_LIMIT: "권한 틀 개수 초과",
  ROLE_TEMPLATE_READONLY: "고칠 수 없는 틀",
  SENDER_LIMIT_EXCEEDED: "발신번호 수 초과",
  SENDER_NOT_ALLOWED: "허용되지 않은 발신번호",
  EMPLOYEE_COUNT_REQUIRED: "종사자 수 입력 필요",
  PHONE_DUPLICATED: "이미 쓰는 번호",
  EMAIL_DUPLICATED: "이미 쓰는 이메일",
  SUPPORT_ACCOUNT_LOGIN_DENIED: "지원 계정 로그인 안 됨",
  SUPPORT_ACCOUNT_READONLY: "지원 계정은 고칠 수 없음",
  SUPPORT_SCOPE_DENIED: "지원 접속에서 못 쓰는 메뉴",
  SUPPORT_SESSION_ENDED: "지원 접속 끝남",
  SUPPORT_CONSENT_REQUIRED: "지원 접근 허용 꺼짐",
  SUPPORT_ACCESS_OFF: "지원 접속 안 열림",
  SUPPORT_PROOF_REQUIRED: "지원 접속 본인 확인 필요",
  SUPPORT_CODE_INVALID: "접속 코드 틀림·만료",
  SUPPORT_SUB_ACCOUNT_LOCKED: "지원 계정 잠김",
  SUPPORT_USERNAME_RESERVED: "쓸 수 없는 아이디",
  AUDIT_UNAVAILABLE: "이용 내역을 남기지 못함",
  AUDIT_QUERY_TOO_BROAD: "조회 조건이 너무 넓음",
  AUDIT_BUSY: "조회가 몰림",
  HTTP_400: "잘못된 요청",
  HTTP_401: "로그인 필요",
  HTTP_403: "권한 없음",
  HTTP_404: "대상 없음",
  HTTP_409: "이미 바뀌었거나 겹침",
  HTTP_429: "요청이 너무 많음",
  unknown: "알 수 없는 이유",
};

/**
 * 바뀐 칸 이름(changes[].f, 점 경로면 마디마다) — inapi diffForAudit 호출·@Audit fields·계정 스키마에서 10-31 grep.
 * mode·perServer 는 보안 설정 카드 이력(securityModes)과 같은 말. 모르는 칸은 「기타 항목」
 */
const FIELD = {
  ...CHANGE_FIELD_LABEL,
  values: "옛 접근 방식",
  perms: "권한",
  roleTemplate: "권한 틀",
  templateName: "틀 이름",
  features: "기능",
  senders: "발신번호",
  employeeCount: "종사자 수",
  accountCount: "계정 수",
  reason: "사유",
  reasonNote: "사유 메모",
  consentState: "고객 지원 접근 허용",
  status: "상태",
  source: "바꾼 곳",
  textVersion: "동의 문구 버전",
  revokeSource: "끈 곳",
  lockedAt: "잠긴 시각",
  lockReason: "잠긴 이유",
  lockSource: "잠근 곳",
  username: "아이디",
  role: "역할",
  mfaRequired: "2차 인증 필수",
  password: "비밀번호",
  ownerId: "대표 계정",
  viewManufacturerId: "연결한 제조사",
  evidenceType: "증빙 종류",
  evidenceFileId: "증빙 파일",
  name: "이름",
  phone: "전화번호",
  mobile: "휴대폰 번호",
  email: "이메일",
  accountType: "계정 종류",
  deptName: "부서",
  duty: "직무",
  memo: "메모",
  type: "종류",
  companyId: "회사",
  deleteYn: "삭제 여부",
  active: "사용 여부",
  isTestCompany: "시험 회사",
  ceo: "대표자",
  cNo: "사업자등록번호",
  birth: "생년월일",
  gender: "성별",
  title: "제목",
  tags: "태그",
  customerName: "고객 이름",
  customField: "사용자 정의 항목",
  planCode: "요금제",
  callbackUrl: "콜백 주소",
  refId: "참조 id",
  at: "시각",
  mfaCode: "인증번호",
  passwordChangeRequired: "비밀번호 변경 필요",
  permsReason: "권한 바꾼 사유",
  permsGrantedBy: "권한 준 사람",
  permsGrantedAt: "권한 준 시각",
  permsApprovedBy: "권한 승인자",
  geoExempt: "국외 접속 예외",
  twoFactorDisabled: "2차 인증 끔",
  twoFactorMethods: "2차 인증 수단",
  twoFactorExemption: "2차 인증 예외",
  twoFactorPhone: "2차 인증 번호",
  twoFactorEmails: "2차 인증 이메일",
};

/**
 * 이 콘솔(admin-certilife)의 화면 위치(라우트 name — clientHeaders screenOf) → 메뉴 이름(사이드메뉴와 같은 말).
 * ponytail: 다른 앱(studio·병원·제조사·리셀러)의 화면 키는 이름표가 없어 「알 수 없는 화면」 — 필요하면 앱별 표를 더한다
 */
const SCREEN = {
  signIn: "로그인",
  dashboard: "대시보드",
  hosManage: "병원 관리",
  hosAccManage: "병원 계정 관리",
  hosCrawlManage: "수집한 병원정보",
  hospitalAdManage: "광고 병원 관리",
  manuManage: "제조사 관리",
  manuAccManage: "제조사 계정 관리",
  MenuManage: "메뉴 관리",
  centralScenarioList: "시나리오 배포",
  centralScenarioDetail: "시나리오 배포 상세",
  dentalLabManage: "기공소 관리",
  dentalLabAccManage: "기공소 계정 관리",
  resellerManage: "리셀러 관리",
  resellerAccManage: "리셀러 계정 관리",
  studioManage: "STUDIO 관리",
  quickReplyMasterManage: "자주 사용하는 답변",
  categoryManage: "카테고리 관리",
  categoryCreate: "카테고리 만들기",
  categoryEdit: "카테고리 수정",
  certificateSurgeryRegistration: "인증서 시술 관리",
  certificateImageManagement: "인증서 이미지 관리",
  laborContracts: "근로계약서 조회",
  consentContracts: "시술동의서 조회",
  customerManage: "환자 정보 조회",
  historyManage: "인증서 발행 내역 조회",
  claraManage: "클라라 발송 대상 조회",
  impersonateCustomer: "대리 로그인",
  subscription: "플라즈맵 구독 관리",
  createSubscription: "플라즈맵 구독 만들기",
  editSubscription: "플라즈맵 구독 수정",
  centerflowSubscription: "센터플로우 구독 관리",
  createCenterflowSubscription: "센터플로우 구독 만들기",
  editCenterflowSubscription: "센터플로우 구독 수정",
  subscriptionProducts: "구독 상품",
  productMasters: "상품 마스터",
  monitoring: "배치 모니터링",
  tokenManage: "인증정보 관리",
  alimtalkResults: "알림톡 전송결과",
  seolbiAlimtalkTracking: "설비 알림톡 추적",
  migrationAfterdoc: "애프터닥 이관",
  migrationKakaoBiz1to1: "카카오 비즈니스 1:1 이관",
  certiAccManage: "접속 계정 관리",
  AppVersionManage: "앱 버전 관리",
  webVersionManage: "웹 버전 관리",
  noticesList: "공지사항 관리",
  noticeWrite: "공지사항 쓰기",
  noticeRead: "공지사항 보기",
  noticeModify: "공지사항 수정",
  whitelist: "보안 설정",
  sendWhitelist: "발송 화이트리스트",
  auditLogs: "이용 내역",
  auditChecks: "이용 내역 점검",
  permObservations: "막혔을 요청",
  geoAllowIps: "국외 허용 IP",
  environments: "API 환경변수 관리",
  couponProviderManage: "쿠폰 공급사 관리",
  manualCouponDistribution: "수동 쿠폰 배포",
  connectorManage: "커넥터 관리",
  ItemList: "아이템 목록",
  AddItem: "아이템 추가",
  ModifyItem: "아이템 수정",
  ItemBulkEdit: "아이템 일괄 수정",
  ProductsList: "상품 목록",
  SystemProductList: "제품 관리",
  SubscriptionPackageList: "구독 상품관리",
  subscriptionManagementV2: "구독관리(통합)",
  paymentHistoryV2: "결제내역(통합)",
  createSubscriptionV2: "구독 만들기(통합)",
  subscriptionDetailV2: "구독 상세(통합)",
  editSubscriptionV2: "구독 수정(통합)",
  promotionManagement: "제조사 프로모션",
  promotionEntries: "프로모션 참여 목록",
  orderManagement: "주문 관리",
  eventManagement: "이벤트 관리",
  createEvent: "이벤트 만들기",
  editEvent: "이벤트 수정",
  eventBlackPatientManage: "메시지 발송 제외 설정",
  eventStatistics: "이벤트 통계",
  aiPromptManagement: "AI 프롬프트 관리",
  reviewManagement: "후기 관리",
  eventParticipationManage: "이벤트 참여 관리",
  storage: "자료실",
  fileUpload: "파일 업로드",
  bannerManagement: "배너 관리",
  reservationManagement: "예약 관리",
  counselorManage: "상담톡 관리",
  callbotExtensionManage: "내선 관리",
  hospitalSearchKeywordManage: "제외 키워드 관리",
  trackingInfo: "추적정보조회",
  trackingInsights: "추적 인사이트",
  qrManagement: "QR코드 관리",
  qrStats: "QR코드 통계",
  qrCodeManagement: "QR코드",
  homepageEditor: "홈페이지 편집",
  chatConsultReport: "채팅 상담 레포트",
};

// 값이 없으면 "", 표에 없으면 기본값(영문 코드는 보이지 않음)
const nameIn = (table, fallback) => (v) => (v ? table[v] || fallback : "");

export const appName = nameIn(APP, "알 수 없는 앱");
export const serviceName = nameIn(SERVICE, "기타 서버");
export const roleName = nameIn(ROLE, "기타");
export const targetTypeName = nameIn(TARGET_TYPE, "대상");
/** 이유(모르는 코드 = 「기타」, 5xx = 서버 오류) */
export const reasonName = (r) => (r ? REASON[r] || (/^HTTP_5\d\d$/.test(r) ? "서버 오류" : "기타") : "");

// 행동 이름 속 서버 이름(「inapi 직접 발송」) — 앞뒤가 영문·숫자·-·_ 가 아닐 때만
const SERVICE_IN_TEXT = new RegExp(`(^|[^\\w-])(${Object.keys(SERVICE).join("|")})(?![\\w-])`, "g");

/**
 * 표시용 행동 이름(사전은 그대로): 서버 이름은 이름표로, 남은 괄호 속 영문 코드는 뺌.
 * 「inapi 직접 발송」 → 「기본 서버 직접 발송」, 「검사 모드 변경(security_settings)」 → 「검사 모드 변경」. (PBX) 같은 대문자 이름은 둠
 */
export const plainActionName = (ko) =>
  String(ko || "")
    .replace(SERVICE_IN_TEXT, (_, pre, s) => pre + SERVICE[s])
    .replace(/\s*\([a-z][\w.-]*\)/g, "")
    .trim();

/** 바뀐 칸 이름: 점 경로는 마디마다(「role.perms」 → 「역할 · 권한」), 모르는 마디는 「기타 항목」, 숫자 마디는 그대로 */
export const fieldName = (f) =>
  f
    ? String(f)
        .split(".")
        .map((seg) => (/^\d+$/.test(seg) ? seg : FIELD[seg] || "기타 항목"))
        .join(" · ")
    : "-";

const raw = (v) => (typeof v === "object" ? JSON.stringify(v) : String(v));
const named = (table) => (v) => table[v] || raw(v);

// 계정·동의 상태 값(inapi 'ACTIVE'·'LOCKED'… 와 지원 동의 'ON'·'OFF')
const STATUS = {
  ACTIVE: "사용 중",
  INACTIVE: "사용 안 함",
  LOCKED: "잠김",
  BLOCKED: "막힘",
  PENDING: "대기",
  SUSPENDED: "정지",
  DELETED: "삭제됨",
  ON: "켬",
  OFF: "끔",
};
const ACCOUNT_TYPE = { ADMIN: "관리자", MEMBER: "직원" };
// 지원 접근 허용을 켜고 끈 곳(inapi support-scope ConsentSource + EXISTING) · 서브 계정 잠금 출처·이유
const CONSENT_SOURCE = {
  IN_APP: "고객사",
  CUSTOMER: "고객사",
  OPERATOR: "운영자",
  RESELLER: "리셀러",
  OFFLINE: "서면 동의",
  EXISTING: "기존 회사 일괄 켜기",
};
const LOCK_SOURCE = { AUTO: "자동", MANUAL: "직접" };
const LOCK_REASON = { CONSENT_REVOKED: "고객 지원 접근 허용 꺼짐" }; // 그 밖은 사람이 적은 글이라 그대로

/** 서버별 값 {서버·앱: 값} → 「기본 서버=막음, CRM 서버=끔」(이용 내역 전용 — 보안 설정 카드는 그대로) */
const perServerName = (o) =>
  o && typeof o === "object" && Object.keys(o).length
    ? Object.entries(o)
        .map(([s, v]) => `${SERVICE[s] || APP[s] || s}=${valueLabel(v)}`)
        .join(", ")
    : "없음";

// 칸(마지막 마디)별 값 이름표. 모드 값은 보안 설정 카드 이력과 같은 말(끔·기록만·막음·켬·허용)
const VALUE_BY_FIELD = {
  mode: valueText,
  values: valueText,
  perServer: perServerName,
  status: named(STATUS),
  role: named(ROLE),
  accountType: named(ACCOUNT_TYPE),
  source: named(CONSENT_SOURCE),
  revokeSource: named(CONSENT_SOURCE),
  lockSource: named(LOCK_SOURCE),
  lockReason: named(LOCK_REASON),
};

/**
 * 바뀐 칸 값 한 개 → 보일 글자. 권한 코드는 권한 이름(permLabels = loadAllPermLabels {code: ko}), 참·거짓·Y/N(…Yn)은 예·아니요,
 * 모드·상태·역할·계정 종류·서버별 값은 이름표. 모르는 값은 그대로(서버가 이미 가린 값만 옴)
 */
export const changeValueText = (f, v, permLabels = {}) => {
  const last = String(f || "").split(".").pop();
  const isPerm = last === "perms";
  if (v == null || v === "" || (isPerm && Array.isArray(v) && !v.length)) return "(없음)";
  if (isPerm) {
    const one = (code) => (typeof code === "string" && permLabels[code]) || String(code);
    return Array.isArray(v) ? v.map(one).join(", ") : one(v);
  }
  if (typeof v === "boolean") return v ? "예" : "아니요";
  if (/Yn$/.test(last) && (v === "Y" || v === "N")) return v === "Y" ? "예" : "아니요";
  return VALUE_BY_FIELD[last] ? VALUE_BY_FIELD[last](v) : raw(v);
};

/** 내려받기 파일 형식 이름(모르면 「파일」) */
export const formatName = (f) => ({ csv: "CSV 파일", xlsx: "엑셀 파일", pdf: "PDF 파일", zip: "ZIP 파일" })[f] || "파일";

/** 보안 설정 대상 id(스위치 키) → 「보안 설정 · 지원 접속(서브 계정)」. 모르는 키는 「보안 설정」만 */
export const securitySettingName = (key) => {
  const def = defOf(key);
  return def ? `보안 설정 · ${def.name}` : "보안 설정";
};

/**
 * 화면 위치(row.screen, 'name#탭'). 이 콘솔(또는 앱 모름)의 아는 키는 메뉴 이름,
 * 아니면 앱을 알 때 「<앱> 화면」(어느 앱에서 했는지는 보이게), 앱도 모르면 「알 수 없는 화면」
 */
export const screenName = (row = {}) => {
  if (!row.screen) return "";
  const app = row.source && row.source.app;
  const mine = !app || app === "admin-certilife" || app === "unknown";
  const known = mine && SCREEN[String(row.screen).split("#")[0]];
  if (known) return known;
  if (!APP[app] || app === "unknown") return "알 수 없는 화면";
  return /화면$/.test(APP[app]) ? APP[app] : `${APP[app]} 화면`;
};
